// FormFill köken türetmeleri — Geçmiş Rapor Aktarımı (Task 15, fix round 1 / R-perf).
//
// NEDEN AYRI BİR HOOK: ilk sürümde FormFill doğrudan `s.responsesByCode` ve
// `s.pending` NESNELERİNE abone oluyordu. Her tuş vuruşunda `pending` yeni bir nesne
// olduğu için FormFill (ve dolayısıyla TÜM QuestionCard'lar) yeniden render ediliyordu
// — aktarımı olmayan projelerde bile. Burada iki kural var:
//
//   1. Abonelikler YALNIZ primitif döndürür (`boolean`, `string`). Zustand Object.is
//      ile karşılaştırdığı için, yazılan metin "dolu/boş" durumunu değiştirmediği
//      sürece hiçbir re-render tetiklenmez.
//   2. Köken yoksa (`provenance === null`) hiçbir türetme koşmaz — imza fonksiyonu
//      da erken çıkar; aktarımsız projelerde maliyet sıfırdır.
//
// Gerçek değerler, imza değiştiğinde `useFormFillStore.getState()` ile TEK sefer
// okunur (memo içinde); abonelik değil, anlık okuma.

import { useMemo } from "react";
import { isFormValueFilled, type ImportSummary, type ResponseProvenance } from "@mahir/shared";
import { useFormFillStore, type FormFillState } from "../stores/form-fill-store";
import {
  filterQuestionCodes,
  latestCreateImport,
  sectionCountsForFilter,
  type ImportQuestionFilter,
} from "../lib/report-import-ui";

/** Soru kartlarına tek prop olarak geçen, render'lar arası STABİL köken bağlamı. */
export interface ProvenanceCardContext {
  entriesByCode: Map<string, ResponseProvenance>;
  importsById: Map<string, ImportSummary>;
  threshold: number;
  /** Projeyi yaratan aktarımın bitiş zamanı — "düzenlendi" karşılaştırması (10A). */
  createFinishedAt: string | null;
  canOpenReport: boolean;
}

export interface ProvenanceDerivations {
  /** Köken yoksa `null` — çağıran taraf tüm köken UI'sını atlar. */
  card: ProvenanceCardContext | null;
  createImport: ImportSummary | null;
  chipCounts: { low: number; partial: number; rejected: number; empty: number };
  /** Aktif çipe uyan kodlar; çip "Tümü" iken `null` (düz liste render edilmez). */
  filteredCodes: string[] | null;
  sectionFilterCounts: Record<string, number> | null;
}

const EMPTY_COUNTS = { low: 0, partial: 0, rejected: 0, empty: 0 } as const;

/**
 * "Dolu yanıt kodları" imzası. Bir tuş vuruşu bir alanı boştan doluya (ya da tersine)
 * çevirmedikçe aynı string döner → re-render yok. `pending` (kaydedilmemiş) değer
 * sunucu değerini gölgeler; çip sayısı 800 ms debounce'ı beklemesin diye.
 */
function filledSignature(s: FormFillState): string {
  if (s.provenance === null) return "";
  const filled: string[] = [];
  for (const [code, r] of Object.entries(s.responsesByCode)) {
    if (s.pending[code] === undefined && isFormValueFilled(r.value)) filled.push(code);
  }
  for (const [code, p] of Object.entries(s.pending)) {
    if (isFormValueFilled(p.value)) filled.push(code);
  }
  return filled.sort().join("|");
}

function valuesFromState(s: FormFillState): Record<string, { value: unknown } | undefined> {
  const out: Record<string, { value: unknown } | undefined> = {};
  for (const [code, r] of Object.entries(s.responsesByCode)) out[code] = { value: r.value };
  for (const [code, p] of Object.entries(s.pending)) out[code] = { value: p.value };
  return out;
}

export function useProvenanceDerivations(args: {
  /** Snapshot sırasında (bölüm order → soru order) etkin soru kodları. */
  questionCodesInOrder: readonly { code: string; sectionId: string }[];
  canOpenReport: boolean;
  /** Client aktörde köken UI'sı hiç yok — türetme de koşmaz. */
  enabled: boolean;
}): ProvenanceDerivations {
  const { questionCodesInOrder, canOpenReport, enabled } = args;

  const provenance = useFormFillStore((s) => s.provenance);
  const provenanceFilter = useFormFillStore((s) => s.provenanceFilter);
  const resolvedRejections = useFormFillStore((s) => s.resolvedRejections);
  const pinnedCode = useFormFillStore((s) => s.pinnedCode);
  // Primitif abonelik — değer değişmedikçe render yok (bkz. dosya başı).
  const signature = useFormFillStore(filledSignature);

  return useMemo<ProvenanceDerivations>(() => {
    if (!enabled || !provenance) {
      return {
        card: null,
        createImport: null,
        chipCounts: { ...EMPTY_COUNTS },
        filteredCodes: null,
        sectionFilterCounts: null,
      };
    }
    // `signature` değiştiğinde (ya da provenance/filtre/pin) TEK sefer okunur.
    void signature;
    const state = useFormFillStore.getState();
    const valuesByCode = valuesFromState(state);
    const allCodes = questionCodesInOrder.map((q) => q.code);

    // 11A — kalıcı kapanış: reddedilen ∧ yanıt dolu → çözülmüş sayılır (oturum içi
    // açık kabul Set'iyle birleşir).
    const effectiveResolved = new Set(resolvedRejections);
    for (const e of provenance.entries) {
      if (e.rejected && isFormValueFilled(valuesByCode[e.questionCode]?.value)) {
        effectiveResolved.add(e.questionCode);
      }
    }

    const chipCounts = {
      low: filterQuestionCodes(allCodes, provenance, valuesByCode, "low", effectiveResolved).length,
      // P6 — kısmi kabul kendi çipinde (inceleme C3); çip yalnız > 0 iken render edilir.
      partial: filterQuestionCodes(allCodes, provenance, valuesByCode, "partial", effectiveResolved)
        .length,
      rejected: filterQuestionCodes(
        allCodes,
        provenance,
        valuesByCode,
        "rejected",
        effectiveResolved,
      ).length,
      empty: filterQuestionCodes(allCodes, provenance, valuesByCode, "empty", effectiveResolved)
        .length,
    };

    let filteredCodes: string[] | null = null;
    let sectionFilterCounts: Record<string, number> | null = null;
    if (provenanceFilter !== "all") {
      const matched = new Set(
        filterQuestionCodes(
          allCodes,
          provenance,
          valuesByCode,
          provenanceFilter,
          effectiveResolved,
        ),
      );
      // 2A — düzenlenen kart blur'a kadar listede kalır.
      filteredCodes = allCodes.filter((c) => matched.has(c) || c === pinnedCode);
      const bySection: Record<string, string[]> = {};
      for (const q of questionCodesInOrder) (bySection[q.sectionId] ??= []).push(q.code);
      sectionFilterCounts = sectionCountsForFilter(bySection, filteredCodes);
    }

    const createImport = latestCreateImport(provenance.imports);
    return {
      card: {
        entriesByCode: new Map(provenance.entries.map((e) => [e.questionCode, e])),
        importsById: new Map(provenance.imports.map((i) => [i.id, i])),
        threshold: provenance.threshold,
        createFinishedAt: createImport?.finishedAt ?? null,
        canOpenReport,
      },
      createImport,
      chipCounts,
      filteredCodes,
      sectionFilterCounts,
    };
  }, [
    enabled,
    provenance,
    provenanceFilter,
    resolvedRejections,
    pinnedCode,
    signature,
    questionCodesInOrder,
    canOpenReport,
  ]);
}

export type { ImportQuestionFilter };
